import React, { useState, useMemo } from 'react'
import {
  MenuItem,
  Select,
  TextField,
  InputAdornment,
  FormControl,
} from '@material-ui/core'
import Checkbox from 'components/common/Checkbox'
import Radio from 'components/common/Radio'
import isEqual from 'lodash/isEqual'
import { injectIntl } from 'react-intl'
import cn from 'classnames'
import { ReactComponent as Vector } from 'assets/img/select-vector.svg'
import { PURPLE, PRIMARY_TEXT } from 'constants/colors'
import {
  StyledClearSelectButton,
  StyledClearSelectMenuItem,
} from 'routes/App/Apps/MyJobs/JobFields/JobFields.styles'
import Loader from 'components/Loading/SmallLoader'
import messages from '../translate'
import {
  StyledMenuItem,
  StyledMenuItemNav,
  StyledSelectInput,
  StyledMenuItemText,
  EmptyState,
  LoaderContainer,
} from '../styles'
import SelectInputBase from '../SelectInputBase'

function SelectInput({
  intl: { formatMessage },
  multiple = false,
  hasSearchField = false,
  fullWidth = false,
  label = formatMessage(messages.select),
  options = [],
  required = false,
  isValidationFailed,
  onChange,
  onClose,
  selectInputRef,
  searchInputRef,
  selectedValue,
  setSelectedValue,
  searchQuery,
  setSearchQuery,
  filteredOptions,
  setFilteredOptions,
  isEmptySelect,
  isSelectInputDisabled,
  onCloseSelectInput,
  header,
  isNavItems,
  onJobNavigate,
  emptyStateText = formatMessage(messages.noRelevantInputs),
  hasSelectAllOption = false,
  startAdornment: StartAdornment = null,
  optionsMessages,
  hasClearOption = false,
  displayLabelIfAllSelected = false,
  onOpen,
  isLoading,
  customWidth,
  optionsToSelectLabel = null,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [displayValue, setDisplayValue] = useState('')
  const defaultValue = useMemo(() => (multiple ? [] : null), [multiple])

  const renderValues = () => {
    if (isEmptySelect) {
      return `${label}${required ? ' *' : ''}`
    }

    let selectedLabel

    if (!multiple) {
      const selected = options.find(({ value }) => value === selectedValue)
      selectedLabel =
        selected &&
        (optionsMessages
          ? formatMessage(optionsMessages[selected.label])
          : selected.label)
    }

    const allOptions = optionsToSelectLabel || options
    const displayLabelCondition =
      allOptions.length === selectedValue?.length && displayLabelIfAllSelected
    const multipleLabel =
      multiple &&
      (displayLabelCondition
        ? label
        : allOptions
            .filter(({ value }) =>
              selectedValue.some(selected => selected === value),
            )
            .map(({ label: optionLabel }) =>
              optionsMessages
                ? formatMessage(optionsMessages[optionLabel])
                : optionLabel,
            )
            .join(', '))

    const displayLabel = multipleLabel || selectedLabel

    if (selectedValue?.length && displayLabel?.length) {
      setDisplayValue(displayLabel)
    }

    return selectedValue?.length && !displayLabel?.length
      ? displayValue
      : displayLabel
  }

  const onChangeSearchField = ({ target: { value: targetValue } }) => {
    const filtered = options.filter(
      ({ label }) =>
        label.toLowerCase().indexOf(targetValue.toLowerCase()) >= 0,
    )

    if (targetValue) {
      filtered.sort(
        ({ label }, { label: labelNext }) =>
          labelNext.startsWith(targetValue) - label.startsWith(targetValue),
      )
    }

    setFilteredOptions(filtered)
    setSearchQuery(targetValue)
  }

  const onChangeSelectInput = ({ target: { value } }) => {
    let newValue = value

    if (
      multiple &&
      Array.isArray(newValue) &&
      newValue.includes('SELECT_ALL')
    ) {
      const allOptions = filteredOptions.map(option => option.value)
      const allSelected = selectedValue.length === allOptions.length

      newValue = allSelected ? [] : allOptions
    }

    setSelectedValue(newValue)

    if (onClose && !multiple) {
      onClose(newValue)
    }

    if (onChange) {
      onChange(newValue)
    }
  }

  const renderSelectAllOption = () => {
    const allOptions = filteredOptions
    const allSelected = selectedValue.length === allOptions.length
    const noneSelected = selectedValue.length === 0
    const someSelected = !allSelected && !noneSelected

    return (
      <StyledMenuItem
        className={cn('menu-item', { active: allSelected || someSelected })}
        onClick={event => event.stopPropagation()}
        value="SELECT_ALL">
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={event => event.preventDefault()}
          color={PURPLE}
        />
        {formatMessage(messages.selectAll)}
      </StyledMenuItem>
    )
  }

  return (
    <StyledSelectInput
      error={isValidationFailed}
      $hasAdornment={!!StartAdornment}
      customWidth={customWidth}>
      <FormControl fullWidth={fullWidth} disabled={isSelectInputDisabled}>
        <Select
          IconComponent={Vector}
          displayEmpty
          multiple={multiple}
          required={required}
          className="job-field-input-box"
          disabled={isSelectInputDisabled}
          value={selectedValue}
          ref={selectInputRef}
          MenuProps={{
            className: cn(
              'triggers-select-dropdown',
              { multiple },
              { 'has-search': hasSearchField },
            ),
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            getContentAnchorEl: null,
          }}
          onChange={event => {
            onChangeSelectInput(event)
          }}
          renderValue={renderValues}
          onOpen={() => {
            setIsOpen(true)
            setTimeout(() => {
              if (searchInputRef.current) {
                searchInputRef.current.focus()
              }
            }, 0)
            if (onOpen) {
              onOpen(setFilteredOptions)
            }
          }}
          onClose={props => {
            setIsOpen(false)
            onCloseSelectInput(props)
          }}
          inputProps={{
            className: 'job-field-input',
          }}
          startAdornment={
            StartAdornment && (
              <InputAdornment disablePointerEvents position="start">
                <StartAdornment color={isOpen ? PURPLE : PRIMARY_TEXT} />
              </InputAdornment>
            )
          }>
          {header && header()}
          {hasSearchField && (
            <MenuItem
              className="jobs-triggers-input"
              onKeyDown={event => {
                event.stopPropagation()
              }}
              button={false}>
              <TextField
                fullWidth
                placeholder={formatMessage(messages.search)}
                value={searchQuery}
                inputProps={{
                  ref: searchInputRef,
                }}
                onClick={event => {
                  event.stopPropagation()
                }}
                onChange={onChangeSearchField}
              />
            </MenuItem>
          )}
          {isLoading && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}
          {!isLoading && !isSelectInputDisabled && !filteredOptions.length && (
            <EmptyState>{emptyStateText}</EmptyState>
          )}
          {hasSelectAllOption &&
            multiple &&
            !!filteredOptions.length &&
            renderSelectAllOption()}
          {hasClearOption && !isEqual(selectedValue, defaultValue) && (
            <StyledClearSelectMenuItem
              onKeyDown={e => {
                e.stopPropagation()
              }}>
              <StyledClearSelectButton
                onClick={e => {
                  e.stopPropagation()

                  onChange(defaultValue)
                }}>
                {formatMessage(messages.clearSelection)}
              </StyledClearSelectButton>
            </StyledClearSelectMenuItem>
          )}
          {filteredOptions.map(({ value, label }) => {
            const isSelected = multiple
              ? selectedValue.some(val => val === value)
              : value === selectedValue
            const optionLabel = optionsMessages
              ? formatMessage(optionsMessages[label])
              : label

            return isNavItems ? (
              <StyledMenuItemNav key={value} className="menu-item">
                <StyledMenuItemText
                  onClick={event => {
                    event.stopPropagation()
                    onJobNavigate({ value, label })
                  }}>
                  {optionLabel}
                </StyledMenuItemText>
              </StyledMenuItemNav>
            ) : (
              <StyledMenuItem
                key={value}
                value={value}
                className={cn('menu-item', { active: isSelected })}>
                {multiple ? (
                  <Checkbox
                    checked={isSelected}
                    onChange={event => {
                      event.preventDefault()
                    }}
                    color={PURPLE}
                  />
                ) : (
                  <Radio
                    checked={isSelected}
                    onChange={event => {
                      event.preventDefault()
                    }}
                    color={PURPLE}
                  />
                )}
                {optionLabel}
              </StyledMenuItem>
            )
          })}
        </Select>
      </FormControl>
    </StyledSelectInput>
  )
}

export default injectIntl(SelectInputBase(SelectInput))
